import type { ImageTextItem, ImageTextRowData } from "@/components/sections/ImageTextRowSection";

function itemFromUnknown(v: unknown): ImageTextItem | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const image_url = String(o.image_url ?? o.imageUrl ?? "").trim();
  const title = String(o.title ?? "").trim();
  const body = String(o.body ?? "").trim();
  const cta_label = String(o.cta_label ?? o.ctaLabel ?? "").trim();
  const cta_url = String(o.cta_url ?? o.ctaUrl ?? "").trim();
  if (!title && !body && !image_url && !cta_label) return null;
  return { image_url, title, body, cta_label, cta_url };
}

function parseItemsField(raw: unknown): ImageTextItem[] {
  if (Array.isArray(raw)) {
    return raw
      .map(itemFromUnknown)
      .filter((x): x is ImageTextItem => x !== null);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map(itemFromUnknown)
          .filter((x): x is ImageTextItem => x !== null);
      }
    } catch {
      // ignore invalid JSON
    }
  }
  return [];
}

/** Merge Arabic text onto English blocks; keep images/CTAs from English when missing in Arabic. */
function mergeItemLists(
  en: ImageTextItem[],
  ar: ImageTextItem[],
): ImageTextItem[] {
  if (ar.length === 0) return en;
  return en.map((item, i) => {
    const tr = ar[i];
    if (!tr) return item;
    return {
      image_url: tr.image_url?.trim() || item.image_url,
      title: tr.title?.trim() || item.title,
      body: tr.body?.trim() || item.body,
      cta_label: tr.cta_label?.trim() || item.cta_label,
      cta_url: tr.cta_url?.trim() || item.cta_url,
    };
  });
}

/**
 * Normalizes CMS image_text_row section data for the public site.
 * Accepts `items` or `items_json` from the API and merges bilingual item lists safely.
 */
export function normalizeImageTextRowData(
  data: Record<string, unknown>,
  dataAr?: Record<string, unknown>,
  lang?: string,
): ImageTextRowData {
  const enItems = parseItemsField(data.items ?? data.items_json);
  const useAr = lang === "ar" && dataAr;
  const arItems = useAr
    ? parseItemsField(dataAr.items ?? dataAr.items_json)
    : [];

  const items = useAr ? mergeItemLists(enItems, arItems) : enItems;

  const pick = (key: string) => {
    if (useAr && dataAr) {
      const ar = dataAr[key];
      if (ar != null && String(ar).trim() !== "") return String(ar);
    }
    const en = data[key];
    return en != null ? String(en) : undefined;
  };

  return {
    heading: pick("heading"),
    subheading: pick("subheading"),
    items,
  };
}
